import type { JSX, Ref } from 'react'

import { Label, Text, Textarea } from '@buffer-mono/popcorn'

import { MAX_ANSWER_LENGTH } from '../FeedbackWidget.constants'

import styles from '../FeedbackWidget.module.css'

interface FeedbackQuestionProps {
  /** Widget id, which the field's own ids are built from. */
  id: string
  /** Names the field in ids and test hooks. */
  name: 'summary' | 'context'
  question: string
  inputRef?: Ref<HTMLTextAreaElement>
  value: string
  error: string | null
  onChange: (value: string) => void
  onBlur: () => void
}

/**
 * One question in the feedback form: a label, an answer, and a counter that
 * shows the cap while someone types. The cap is set on the textarea rather than
 * checked on submit, so nobody writes a long answer only to be told at the end
 * that it does not fit.
 */
const FeedbackQuestion = ({
  id,
  name,
  question,
  inputRef,
  value,
  error,
  onChange,
  onBlur,
}: FeedbackQuestionProps): JSX.Element => {
  const fieldId = `${id}:${name}`
  const errorId = `${fieldId}-error`

  return (
    <div className={styles.field}>
      <div className={styles.labelRow}>
        <Label htmlFor={fieldId}>{question}</Label>
        {/* Hidden from assistive technology: the textarea carries
            aria-required, so announcing this too would say it twice. */}
        <Text size="sm" color="subtle" className={styles.required} aria-hidden>
          Required
        </Text>
      </div>
      <Textarea
        ref={inputRef}
        id={fieldId}
        rows={3}
        resize="auto"
        aria-required
        aria-describedby={error ? errorId : undefined}
        aria-invalid={!!error}
        maxLength={MAX_ANSWER_LENGTH}
        value={value}
        onChange={(event): void => onChange(event.target.value)}
        onBlur={onBlur}
        className={styles.textarea}
      />
      <div className={styles.fieldFooter}>
        {error && (
          <Text
            color="critical"
            size="sm"
            role="alert"
            id={errorId}
            data-testid={`${name}-error`}
          >
            {error}
          </Text>
        )}
        {/* Hidden from assistive technology: the textarea's own maxLength
            already carries the limit, and a count that changes on every
            keystroke would be announced over the person typing. */}
        <Text
          size="sm"
          color="subtle"
          className={styles.counter}
          aria-hidden
          data-testid={`${name}-counter`}
        >
          {value.length}/{MAX_ANSWER_LENGTH}
        </Text>
      </div>
    </div>
  )
}

export { FeedbackQuestion }
